import { useEffect, useRef, useState } from 'react';
import './style.scss';
import flattenDeep from 'lodash/flattenDeep';
import orderBy from 'lodash/orderBy';
import { TranslationService } from '../../services/translation.service';
import { useTexts } from '../../hooks/texts.hook';
import LineAutocomplete, { type ILineAutocompleteState } from '../Shared/LineAutocomplete';
import type { ILine } from '../../models/line.model';
import MunicipalitySelector, { type IMunicipalitySelector } from '../Shared/MunicipalitySelector';
import type { IMunicipality } from '../../models/municipality.model';
import TransportModeFilter from '../Shared/TransportModeFilter';
import { TransportMode } from '../../models/transport-mode.enum';
import ClearButtonComponent from '../Shared/ClearButton';
import type { IAutocomplete } from '../Shared/AutocompleteBase';
import { AnalyticsService } from '../../services/analytics-service';
import { LineHelper } from '../../helpers/line.helper';
import VersionedLineApiModel from './models/versioned-line.api-model';
import Alert from '../Shared/Alert';
import { SanitizerService } from '../../services/sanitizer.service';
import TimetablesLine from './components/TimetablesLine';
import ValidationInfo from '../../validation/validation-info';
import useFetch, { Status } from '../../hooks/fetch.hook';
import { VasttrafikGidHelper } from '../../helpers/vasttrafik-gid.helper';

// === FUNCTIONS ===
// See NV-2822 and NV-3040
function filterLineAutocompleteSuggestions(line: ILine): boolean {
  // Göteborgs Spårvägar Ersättningstrafik contractorId
  const gsersContractorId = 18;
  // Västtrafik Ersättningstrafik contractorId
  const vtersContractorId = 315;
  // Trafikhuvudman för Västtrafik
  const vasttrafikPublicTransportAuthorityId = '014';
  return VasttrafikGidHelper.getPublicTransportAuthority(line.gid) === vasttrafikPublicTransportAuthorityId && line.contractorId !== gsersContractorId && line.contractorId !== vtersContractorId;
}

function filterBasedOnLine(l: ILine, selectedLine: ILine | undefined): boolean {
  if (!selectedLine) return true;
  return LineHelper.areLinesPracticallyTheSame(l, selectedLine);
}

function filterBasedOnMunicipalities(l: ILine, selectedMunicipalities: IMunicipality[]): boolean {
  if (!selectedMunicipalities || selectedMunicipalities.length === 0) return true;
  return selectedMunicipalities.some((m) => l.municipalities?.some((m2) => m2.gid === m.gid));
}

function filterBasedOnTransportMode(l: ILine, selectedTransportModes: TransportMode[]): boolean {
  return selectedTransportModes.some((tm) => tm === l.transportMode);
}

function processReceivedData(data: VersionedLineApiModel[]): ILine[] {
  const allVersionedLines = data.map(VersionedLineApiModel.mapToBusinessModel);
  // Flatten versionedLines to simple lines and filter relevant lines (see NV-2749)
  const relevantLines = LineHelper.getRelevantLines(flattenDeep(allVersionedLines.map((v) => v.versions)));
  // Order lines (no query order on technical linenumber)
  return orderBy(relevantLines, [(l) => parseInt(l.shortName, 10), (l) => l.technicalLineNumber]);
}

function Timetables() {
  const { t } = useTexts();

  // === CONSTANTS ===
  const availableTransportModes = [TransportMode.bus, TransportMode.ferry, TransportMode.taxi, TransportMode.tram, TransportMode.train];

  // === STATE ===
  const [lineAutocompleteState, setLineAutocompleteState] = useState<ILineAutocompleteState>({
    value: undefined,
    validationInfo: new ValidationInfo(),
  });

  const [selectedMunicipalities, setSelectedMunicipalities] = useState<IMunicipality[]>([]);
  const [selectedTransportModes, setSelectedTransportModes] = useState<TransportMode[]>([TransportMode.bus, TransportMode.ferry, TransportMode.taxi, TransportMode.tram, TransportMode.train]);
  const [{ status: getLinesStatus, data: getLinesData }] = useFetch<VersionedLineApiModel[]>('/api/timetables/lines');
  const [filteredLines, setFilteredLines] = useState<ILine[] | undefined>(undefined);

  // === REFS ===
  const lineAutocompleteRef = useRef<IAutocomplete>(null);
  const municipalitySelectorRef = useRef<IMunicipalitySelector>(null);

  // === EVENT HANDLERS ===
  function handleLineAutocompleteSelect(line: ILine | undefined): void {
    setLineAutocompleteState({
      value: line,
      validationInfo: new ValidationInfo(),
    });
    if (line) {
      AnalyticsService.addEvent(AnalyticsService.Timetables, 'Sökalternativ - Linje', `${LineHelper.getLineNameAndDirection(line)}, ${line.gid}`);
    }
  }

  async function handleMunicipalitySelectorChange(municipalities: IMunicipality[]): Promise<void> {
    setSelectedMunicipalities(municipalities);
    if (selectedMunicipalities.length !== 0) {
      AnalyticsService.addEvent(AnalyticsService.Timetables, 'Sökalternativ - Kommun', selectedMunicipalities.map((m) => m.name).toString());
    }
  }

  function handleTransportModeFilterChange(transportModes: TransportMode[]): void {
    setSelectedTransportModes(transportModes);
    if (transportModes.length !== 4) {
      AnalyticsService.addEvent(AnalyticsService.Timetables, 'Sökalternativ - Trafikslag', transportModes.map((transportMode: TransportMode) => transportMode.toString()).toString());
    }
  }

  function handleClearClick(): void {
    lineAutocompleteRef.current?.clear();
    setLineAutocompleteState({
      value: undefined,
      validationInfo: new ValidationInfo(),
    });
    municipalitySelectorRef.current?.clear();
    setSelectedMunicipalities([]);
    setSelectedTransportModes(availableTransportModes);
    setFilteredLines(undefined);
    AnalyticsService.addEvent(AnalyticsService.Timetables, 'Klick på återställ', '');
  }

  function handleSearchButtonClick(): void {
    const anyFilterSelected = !!lineAutocompleteState.value || selectedMunicipalities?.length !== 0 || selectedTransportModes.length !== availableTransportModes.length;
    const allLines = getLinesStatus !== Status.success || !getLinesData ? [] : processReceivedData(getLinesData);
    const filteredLines = !anyFilterSelected
      ? allLines
      : allLines
          .filter((l) => filterBasedOnLine(l, lineAutocompleteState.value))
          .filter((l) => filterBasedOnMunicipalities(l, selectedMunicipalities))
          .filter((l) => filterBasedOnTransportMode(l, selectedTransportModes));
    if (filteredLines.length === 0) {
      AnalyticsService.addEvent(AnalyticsService.Timetables, AnalyticsService.ZeroResultsAction, '');
    }
    setFilteredLines(filteredLines);
  }

  // === EFFECTS ===
  useEffect(() => {
    if (getLinesStatus === Status.failed) {
      AnalyticsService.addEvent(AnalyticsService.Timetables, AnalyticsService.ErrorAction, `Hämta tidtabeller - ${AnalyticsService.InternalErrorLabel}`);
    }
  }, [getLinesStatus]);

  return (
    <div className="timetables">
      <div className="timetables__filters shadow-sm rounded p-sm-3 mb-4">
        <div className="mb-3">
          <LineAutocomplete
            id="line-autocomplete"
            /* @ts-expect-error ts(2322) */
            ref={lineAutocompleteRef}
            labelClassName="form-label"
            labelText={TranslationService.get('timetablespage.searchlinelabel')}
            filterResults={filterLineAutocompleteSuggestions}
            noResultsText={TranslationService.get('timetablespage.searchlinenoresultstext') || TranslationService.get('common.autocomplete.noresults.default')}
            placeholderText={TranslationService.get('timetablespage.searchlineplaceholdertext')}
            state={lineAutocompleteState}
            onSelect={handleLineAutocompleteSelect}
          />
        </div>
        <div className="mb-3">
          <label htmlFor="municipality-selector" className="form-label">
            {TranslationService.get('timetablespage.municipalitylabel')}
          </label>
          <MunicipalitySelector
            id="municipality-selector"
            ref={municipalitySelectorRef}
            placeholderText={TranslationService.get('timetablespage.municipalityplaceholdertext')}
            noResultsText={TranslationService.get('timetablespage.municipalitynoresultstext') || TranslationService.get('common.autocomplete.noresults.default')}
            onChange={handleMunicipalitySelectorChange}
            selectedMunicipalities={selectedMunicipalities}
          />
        </div>
        <fieldset className="mb-3">
          <legend>{TranslationService.get('timetablespage.transportmodelabel')}</legend>
          <TransportModeFilter availableTransportModes={availableTransportModes} selectedTransportModes={selectedTransportModes} onChange={handleTransportModeFilterChange} />
        </fieldset>
        <div className="mb-3">
          <ClearButtonComponent onClick={handleClearClick} />
        </div>
        <button type="button" className="btn btn-primary w-100" onClick={handleSearchButtonClick}>
          {TranslationService.get('timetablespage.searchtimetables')}
        </button>
      </div>
      <div>
        <Alert show={getLinesStatus === Status.failed} heading={t.generalError} closable={false} />
        <div aria-live="polite">
          {getLinesStatus === Status.success && filteredLines && (
            <h2>
              {filteredLines.length === 0 && (
                <span>
                  <span className="visually-hidden">{filteredLines.length}</span> {TranslationService.get('timetablespage.timetables')}
                </span>
              )}
              {filteredLines.length > 0 && (
                <span>
                  {`${filteredLines.length} ${
                    filteredLines.length === 1 ? TranslationService.get('timetablespage.timetable').toLowerCase() : TranslationService.get('timetablespage.timetables').toLowerCase()
                  }`}
                </span>
              )}
            </h2>
          )}
          {getLinesStatus === Status.success &&
            filteredLines &&
            (filteredLines.length === 0 ? (
              <div dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('timetablespage.nomatchinglinesfoundtext')) }}></div>
            ) : (
              filteredLines.map((line, index) => <TimetablesLine key={(line.gid ?? '') + index} line={line} className={index < filteredLines.length - 1 ? 'mb-3' : ''} />)
            ))}
        </div>
      </div>
    </div>
  );
}

export default Timetables;
